import { COMPANY } from '../../lib/company';
import { detectLegalLang, type LegalLang } from '../../lib/legalLang';
import { CompanyDetails, PrevailsNote, UpdatedLine } from './LegalChrome';

/** The Host Agreement. Portuguese is the authoritative text; English is a courtesy translation. */
export function HostAgreementContent({ lang = detectLegalLang() }: { lang?: LegalLang }) {
  return lang === 'pt' ? <HostPt /> : <HostEn />;
}

function HostEn() {
  return (
    <div className="prose-legal">
      <UpdatedLine lang="en" />

      <h2>The short version</h2>
      <ul>
        <li>You're an <strong>independent local host</strong>, not a Sekretly employee. You run your own thing; we connect you to travellers whose taste fits.</li>
        <li>Travellers pay through <strong>Stripe</strong>. We collect, keep our commission, and pay you the rest via Stripe Connect.</li>
        <li>You handle your own taxes, licences, and insurance.</li>
        <li>Protect travellers' data and treat people well. Keep it on-platform.</li>
        <li>Anything you contribute to the platform (recommendations, content) can be used by Sekretly, including to power our curation and AI.</li>
        <li>Either side can end this, and there are rules about doing right by travellers you've already committed to.</li>
      </ul>

      <h2>Parties</h2>
      <p>This Agreement is between you and:</p>
      <CompanyDetails lang="en" />

      <h2>1. Relationship</h2>
      <p>You act as an independent contractor. Nothing creates an employment, agency, or partnership relationship. You control how, when, and whether you provide host services. You are responsible for your own taxes, social security, VAT, and any required licences.</p>

      <h2>2. Eligibility and onboarding</h2>
      <p>You must be at least 18 and have the legal right to provide your services. Sekretly may screen, vet, or request background checks. You must create a Stripe Connect account to receive payments.</p>

      <h2>3. Host services and standards</h2>
      <p>You provide local curation and hosting services to matched travellers. You agree to act professionally, honour commitments, respond in reasonable time, provide accurate recommendations, and not discriminate. You are responsible for the safety of experiences you provide.</p>

      <h2>4. Payments and commission</h2>
      <p>Travellers pay through Stripe. Sekretly collects the payment, deducts its commission and processing fees, and settles the balance to you via Stripe Connect. The commission that applies is shown to you in the app. You must process all Sekretly-introduced bookings through the platform.</p>

      <h2>5. Data protection</h2>
      <p>You receive only the traveller information needed for the engagement. You will keep it confidential, not sell it, not use it for your own marketing, and delete it when the engagement ends or when requested. You will notify Sekretly promptly at {COMPANY.email} of any data breach affecting traveller data. How Sekretly handles personal data is described in the <a href="/legal/privacy">Privacy Notice</a>.</p>

      <h2>6. Content, IP, and AI</h2>
      <p>You grant Sekretly a worldwide, perpetual, royalty-free licence to use your contributed content (recommendations, place data, media) to operate, promote, and improve the Service and to train our AI systems. The Sekretly platform, brand, Alma, and curated databases are owned by Sekretly.</p>

      <h2>7. Confidentiality</h2>
      <p>You will keep confidential any non-public information you learn through Sekretly, including traveller information, commercial terms, and platform know-how.</p>

      <h2>8. Anti-circumvention</h2>
      <p>For travellers introduced through Sekretly, you will not take the relationship off-platform to avoid commission, solicit off-platform bookings, or encourage them to leave Sekretly.</p>

      <h2>9. Warranties, liability, and indemnity</h2>
      <p>You warrant that you will provide services with reasonable skill and care. You indemnify Sekretly from claims arising from your services, content, or breach of this Agreement. You are responsible for arranging your own insurance.</p>

      <h2>10. Suspension and termination</h2>
      <p>Either party may terminate with notice. Sekretly may suspend access immediately for breach or safety concerns. On termination, you will fulfil or hand over existing commitments to travellers.</p>

      <h2>11. Governing law and disputes</h2>
      <p>This Agreement is governed by Portuguese law. Disputes are for the courts of {COMPANY.courts}, Portugal, without prejudice to any mandatory rule that applies to you.</p>

      <PrevailsNote lang="en" />
      <p className="text-xs text-app-muted mt-2 italic">Sekretly — curated by people, not feeds.</p>
    </div>
  );
}

function HostPt() {
  return (
    <div className="prose-legal">
      <UpdatedLine lang="pt" />

      <h2>A versão curta</h2>
      <ul>
        <li>É um <strong>anfitrião local independente</strong>, e não um trabalhador da Sekretly. Gere a sua atividade; nós pomo-lo em contacto com viajantes cujo gosto combina consigo.</li>
        <li>Os viajantes pagam através da <strong>Stripe</strong>. Nós recebemos, ficamos com a nossa comissão e pagamos-lhe o restante através da Stripe Connect.</li>
        <li>Os seus impostos, licenças e seguros são da sua responsabilidade.</li>
        <li>Proteja os dados dos viajantes e trate bem as pessoas. Mantenha tudo na plataforma.</li>
        <li>Tudo o que contribuir para a plataforma (recomendações, conteúdos) pode ser usado pela Sekretly, incluindo na nossa curadoria e IA.</li>
        <li>Qualquer das partes pode terminar o acordo, e há regras para honrar os compromissos já assumidos com viajantes.</li>
      </ul>

      <h2>Partes</h2>
      <p>Este Acordo é celebrado entre si e:</p>
      <CompanyDetails lang="pt" />

      <h2>1. Relação entre as partes</h2>
      <p>Atua como prestador de serviços independente. Nada neste Acordo cria uma relação laboral, de agência ou de sociedade. Decide como, quando e se presta serviços de anfitrião. É responsável pelos seus impostos, segurança social, IVA e licenças necessárias.</p>

      <h2>2. Elegibilidade e integração</h2>
      <p>Tem de ter pelo menos 18 anos e o direito legal de prestar os seus serviços. A Sekretly pode selecionar, validar ou solicitar verificações de antecedentes. Tem de criar uma conta Stripe Connect para receber pagamentos.</p>

      <h2>3. Serviços de anfitrião e padrões</h2>
      <p>Presta serviços de curadoria e de acolhimento local aos viajantes que lhe são associados. Compromete-se a agir de forma profissional, a honrar os compromissos, a responder em prazo razoável, a dar recomendações rigorosas e a não discriminar. É responsável pela segurança das experiências que proporciona.</p>

      <h2>4. Pagamentos e comissão</h2>
      <p>Os viajantes pagam através da Stripe. A Sekretly recebe o pagamento, deduz a sua comissão e os custos de processamento e liquida o restante consigo através da Stripe Connect. A comissão aplicável é-lhe mostrada na aplicação. Tem de processar através da plataforma todas as reservas que a Sekretly lhe apresente.</p>

      <h2>5. Proteção de dados</h2>
      <p>Recebe apenas a informação do viajante necessária para a prestação do serviço. Compromete-se a mantê-la confidencial, a não a vender, a não a usar para o seu próprio marketing e a apagá-la quando o serviço terminar ou quando lhe for pedido. Deve notificar de imediato a Sekretly, através de {COMPANY.email}, de qualquer violação de dados que afete dados de viajantes. A forma como a Sekretly trata dados pessoais está descrita na <a href="/legal/privacy">Política de Privacidade</a>.</p>

      <h2>6. Conteúdos, propriedade intelectual e IA</h2>
      <p>Concede à Sekretly uma licença mundial, perpétua e gratuita para usar os conteúdos que contribuir (recomendações, dados de locais, multimédia) na operação, promoção e melhoria do Serviço e no treino dos nossos sistemas de IA. A plataforma Sekretly, a marca, a Alma e as bases de dados curadas pertencem à Sekretly.</p>

      <h2>7. Confidencialidade</h2>
      <p>Compromete-se a manter confidencial toda a informação não pública que conheça através da Sekretly, incluindo informação de viajantes, condições comerciais e conhecimento da plataforma.</p>

      <h2>8. Não contornar a plataforma</h2>
      <p>Quanto a viajantes apresentados pela Sekretly, compromete-se a não levar a relação para fora da plataforma para evitar a comissão, a não solicitar reservas fora da plataforma e a não os incentivar a deixar a Sekretly.</p>

      <h2>9. Garantias, responsabilidade e indemnização</h2>
      <p>Garante que prestará os serviços com perícia e cuidado razoáveis. Indemniza a Sekretly por reclamações decorrentes dos seus serviços, conteúdos ou do incumprimento deste Acordo. É responsável por contratar o seu próprio seguro.</p>

      <h2>10. Suspensão e cessação</h2>
      <p>Qualquer das partes pode terminar o Acordo mediante aviso. A Sekretly pode suspender o acesso de imediato em caso de incumprimento ou por razões de segurança. Na cessação, cumprirá ou transmitirá os compromissos existentes com viajantes.</p>

      <h2>11. Lei aplicável e litígios</h2>
      <p>Este Acordo rege-se pela lei portuguesa. Os litígios são da competência dos tribunais de {COMPANY.courts}, Portugal, sem prejuízo de qualquer norma imperativa que lhe seja aplicável.</p>

      <PrevailsNote lang="pt" />
      <p className="text-xs text-app-muted mt-2 italic">Sekretly — curada por pessoas, não por feeds.</p>
    </div>
  );
}
